<template>
	<div class="clients-panel">
		<div 
			v-if="isContactsDenied" 
			class="clients-panel__no-access"
		>
			<app-icon
				class="clients-panel__no-access-icon"
				name="invalid"
				width="24px"
				color="#979797"
			/>
			<div class="clients-panel__no-access-label">
				{{ deniedText }}
			</div>
		</div>
		<template v-else>
			<clients-block-panel-filters
				v-model:searchStr="filters.searchStr"
				:searchedContactId="searchedContactId"
				:contacts-types="contactsTypesList"
				:selected-tags="selectedTags"
				@on-search="searchContacts"
				@update-searchedContactId="onChangeOption"
				@update:selected-tags="onSelectedTagsUpdate"
				@drop-tag="dropTag"
			/>
			<clients-block-panel-list
				@on-get-partial="fetchPartial"
				:search-text="appliedSearchText"
			/>
		</template>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	ref,
	watch
} from "vue";
import {clientPageStore} from "@/store/client-page";
import {useRoute} from "vue-router";
import ClientsBlockPanelFilters from "@/components/main/client-block-page/ClientsBlockPanelFilters.vue";
import ClientsBlockPanelList from "@/components/main/client-block-page/ClientsBlockPanelList.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { provideContactsContext } from "@/composables/useContactsContext";
import { useContactsAccess } from "@/composables/useContactsAccess";
import { RequestAddressContainer } from "@/composables/useAddressRequest";

type Option = {
	id: number | null,
	label: string,
	type: number,
	value: number | string
}

const route = useRoute()
const clientStore = clientPageStore()
const { isContactsDenied, deniedText } = useContactsAccess()
const clientId = computed(() => parseInt(route.query.id as string))
const searchedContactId = ref<Option | null>(null)
const selectedTags = ref<TagWithFrequency[]>([])
const filters = ref({
	searchType: null as number | null,
	searchStr: '',
	inputContainer: null as RequestAddressContainer | null,
	toSkip: 0,
	count: 30
})
const appliedSearchText = ref('')

const client = computed(() => clientStore.openedClient)
const contactsTypesList = computed(() => {
	if (!client.value) return []

	const options: Option[] = [
		{
			id: null,
			label: "Все контакты системы",
			type: 0,
			value: 'all-system'
		}
	]

	options.push({
		id: client.value.Id,
		label: 'Все контакты холдинга',
		type: 1,
		value: 'all-holding'
	})

	options.push({
		id: client.value.Id,
		label: client.value.Name,
		type: 2,
		value: 'main-client'
	})

	client.value?.UnionClients.forEach((contact, index) => {
		options.push({
			id: contact.Id,
			label: contact.Name,
			type: 2,
			value: 4 + index
		})
	})

	return options
})

const buildRequest = () => ({
	...filters.value,
	tags: selectedTags.value.length ? selectedTags.value.map(t => t.Id) : null,
	clientId: filters.value.searchType
		? searchedContactId.value?.id as number
		: undefined,
})

const searchContacts = async (address?: RequestAddressContainer | null) => {
	if (address !== undefined) {
		filters.value.inputContainer = address
	}
	filters.value.searchType = searchedContactId.value?.type ?? null
	filters.value.toSkip = 0
	appliedSearchText.value = filters.value.searchStr
	await clientStore.getContactsForClient(buildRequest())
}

const fetchPartial = async () => {
	if (
		clientStore.clientContactsLoading
		|| clientStore.clientContactsPartialLoading
		|| !clientStore.canLoadNextContacts
	) return
	filters.value.toSkip += filters.value.count
	await clientStore.getContactsForClient(buildRequest())
}

const onChangeOption = async (option: Option) => {
	searchedContactId.value = option
	await searchContacts()
}

const onSelectedTagsUpdate = (next: TagWithFrequency[]) => {
	const wasEmpty = !selectedTags.value.length
	selectedTags.value = next
	if (!next.length && !wasEmpty) {
		searchContacts()
	}
}

const dropTag = (tag: TagWithFrequency) => {
	selectedTags.value = selectedTags.value.filter(t => t.Id !== tag.Id)
	if (!selectedTags.value.length) {
		searchContacts()
	}
}

provideContactsContext({
	context: 'client',
	get attachClientId() {
		return clientStore.openedClient?.Id ?? null
	},
	refetchContacts: () => searchContacts(),
})

watch(client, async (newVal) => {
	if (newVal) {
		filters.value.toSkip = 0
		selectedTags.value = []
		searchedContactId.value = {
			id: client.value?.Id || 0,
			label: client.value?.Name || '',
			type: 2,
			value: 'main-client'
		}
		filters.value.searchType = 2
		await searchContacts()
	}
})
</script>

<style scoped lang="scss">
.clients-panel {
	display: flex;
	flex-direction: column;
	max-height: 100%;
	height: 100%;
	width: 100%;

	&__no-access {
		display: flex;
		flex: 1;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 24px;
		text-align: center;
		background: #fff;
		border-radius: 8px;

		&-label {
			color: #979797;
			font-size: 16px;
			font-weight: 600;
			line-height: 20px;
		}
	}
}
</style>
